import { useState } from "react";
import { toast } from "react-toastify";
import { Button } from "../../../../components/ui/button";
import { ErrorBanner } from "../../hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../components/QueueLayout";
import { QueueTable, Td } from "../components/table";
import { CopyHash } from "../components/CopyHash";
import { FormDialog } from "../components/FormDialog";
import {
  usePendingDeposits,
  useNotCreditedDeposits,
  useCreditDeposit,
  useRefundDeposit,
} from "../api/hooks";
import { formatDateTime } from "../../../../lib/format";

// The two ways a deposit needs a human.
//
// PENDING      money arrived, the MT5 credit failed. Crediting it here runs the
//              exact same credit the webhook would have run.
// NOT CREDITED money arrived late or below the minimum, so it will never be
//              credited. The refund happens outside this system; the button only
//              records that it was done.
//
// Both are customer support on the same object, so they are tabs on one page.
// Sweeping is treasury work and lives on its own screen.

const TABS = [
  { value: "pending", label: "Pending credit" },
  { value: "not-credited", label: "Not credited" },
];

// Money arrives as decimal strings. Integer cents, read straight out of the string
// so nothing is ever added or subtracted as a float. Plain numbers, not BigInt —
// this project's eslint environment has no BigInt global.
const toCents = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [whole, frac = ""] = text.split(".");
  return Number(whole) * 100 + Number(frac.padEnd(2, "0"));
};

const money = (value) => {
  const cents = toCents(value);
  return cents == null
    ? "—"
    : `${Math.floor(cents / 100).toLocaleString()}.${String(cents % 100).padStart(2, "0")}`;
};

// arrivedAt is documented as possibly null and should not be assumed a date.
const maybeDate = (iso) => (iso ? formatDateTime(iso) : "—");

const PENDING_COLUMNS = [
  { label: "Arrived" },
  { label: "Ref", align: "right" },
  { label: "Login", align: "right" },
  { label: "Amount", align: "right" },
  { label: "", align: "right", sticky: true },
];

const NOT_CREDITED_COLUMNS = [
  { label: "Created" },
  { label: "Ref", align: "right" },
  { label: "Login", align: "right" },
  { label: "Amount", align: "right" },
  { label: "Payments" },
  { label: "", align: "right", sticky: true },
];

// The evidence behind a not-credited row: what actually landed on chain.
function ArrivedPayments({ payments }) {
  if (!payments?.length) {
    return <span className="text-muted-foreground/40">—</span>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {payments.map((p) => (
        <div
          key={`${p.txHash}-${p.logIndex}`}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
        >
          <span className="font-mono text-foreground">{money(p.amount)}</span>
          <CopyHash
            value={p.txHash}
            href={`https://bscscan.com/tx/${p.txHash}`}
            head={8}
            tail={6}
          />
          <span className="text-muted-foreground/70">
            from <CopyHash value={p.fromAddress} head={6} tail={4} />
          </span>
          <span className="text-muted-foreground/50">{maybeDate(p.arrivedAt)}</span>
        </div>
      ))}
    </div>
  );
}

export function DepositQueuePage() {
  const [tab, setTab] = useState("pending");

  const pending = usePendingDeposits();
  const notCredited = useNotCreditedDeposits();
  const credit = useCreditDeposit();
  const refund = useRefundDeposit();

  // { row, action } — the row awaiting confirmation and which action.
  const [confirming, setConfirming] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const isPending = tab === "pending";
  const query = isPending ? pending : notCredited;
  const busy = credit.isPending || refund.isPending;

  const close = () => {
    setConfirming(null);
    setAcknowledged(false);
  };

  const run = () => {
    const { row, action } = confirming;
    const isCredit = action === "credit";
    const mutation = isCredit ? credit : refund;

    mutation.mutate(row.id, {
      onSuccess: (data) =>
        toast.success(
          isCredit
            ? `Credited #${row.login}${data?.mt5Ticket ? ` · ticket ${data.mt5Ticket}` : ""}`
            : `Marked #${row.login} as refunded`,
        ),
      onError: (error) => toast.error(error?.message || "That did not work"),
      onSettled: close,
    });
  };

  const row = confirming?.row;
  const isCredit = confirming?.action === "credit";

  return (
    <QueueLayout
      title="Deposit"
      accent="exceptions"
      description="Deposits that need a human. Money has arrived in every row here — either the credit did not reach MT5, or the payment was too late or too small to credit at all."
      statusOptions={TABS}
      status={tab}
      onStatus={setTab}
      isFetching={query.isFetching}
      onRefresh={() => query.refetch()}
    >
      {query.error ? (
        <ErrorBanner error={query.error} onRetry={() => query.refetch()} />
      ) : isPending ? (
        <QueueTable
          columns={PENDING_COLUMNS}
          isLoading={pending.isLoading}
          rows={pending.data}
          renderRow={(d) => (
            <tr key={d.id} className="transition-colors hover:bg-white/[0.02]">
              <Td className="whitespace-nowrap font-mono text-muted-foreground">
                {maybeDate(d.arrivedAt)}
              </Td>
              <Td align="right" className="font-mono text-muted-foreground">{d.ref}</Td>
              <Td align="right" className="font-mono">{d.login}</Td>
              <Td align="right" className="font-mono font-semibold text-ginox-green">
                {money(d.amount)}
              </Td>
              <Td align="right" sticky>
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => setConfirming({ row: d, action: "credit" })}
                >
                  Credit
                </Button>
              </Td>
            </tr>
          )}
        />
      ) : (
        <QueueTable
          columns={NOT_CREDITED_COLUMNS}
          isLoading={notCredited.isLoading}
          rows={notCredited.data}
          renderRow={(d) => (
            <tr key={d.id} className="transition-colors hover:bg-white/[0.02]">
              <Td className="whitespace-nowrap font-mono text-muted-foreground">
                {maybeDate(d.createdAt)}
              </Td>
              <Td align="right" className="font-mono text-muted-foreground">{d.ref}</Td>
              <Td align="right" className="font-mono">{d.login}</Td>
              <Td align="right" className="font-mono font-semibold">{money(d.amount)}</Td>
              <Td>
                <ArrivedPayments payments={d.arrivedPayments} />
              </Td>
              <Td align="right" sticky>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => setConfirming({ row: d, action: "refund" })}
                >
                  Mark refunded
                </Button>
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog
        open={Boolean(confirming)}
        onOpenChange={(next) => !next && close()}
        title={isCredit ? "Credit this deposit" : "Mark this deposit as refunded"}
        description={
          isCredit
            ? "Runs the same credit the webhook would have run and emails the user. The money reaches their MT5 account immediately."
            : "This only records that you have already sent the money back. It moves nothing itself."
        }
        onSubmit={run}
        submitLabel={isCredit ? "Credit" : "Mark refunded"}
        submitDisabled={!isCredit && !acknowledged}
        loading={busy}
      >
        {row ? (
          <div className="space-y-3 rounded-md border border-border/70 bg-white/[0.02] p-4">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Account</span>
              <span className="font-mono text-sm">#{row.login}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Amount</span>
              <span className="font-mono text-base font-semibold text-ginox-green">
                {money(row.amount)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Ref</span>
              <span className="font-mono text-sm text-muted-foreground">{row.ref}</span>
            </div>
          </div>
        ) : null}

        {/* Refunding happens outside this system, so the button is a record of
            something already done — it takes a deliberate second action, the same
            as marking a withdrawal sent. Crediting needs no tickbox: it performs
            the transfer itself rather than claiming one happened. */}
        {!isCredit ? (
          <label className="flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-emerald-500"
            />
            <span className="text-xs text-muted-foreground">
              I have already sent this money back to the user.
            </span>
          </label>
        ) : null}
      </FormDialog>
    </QueueLayout>
  );
}
